import React, { useState, useRef, useEffect, useCallback } from "react";
import { MessageCircle, X, Send, Bot, User, Loader2, RotateCcw, Mic, MicOff, Volume2, VolumeX } from "lucide-react";
import { API } from "../App";

const SESSION_TIMEOUT_MS = 15 * 60 * 1000;

const getSpeechRecognition = () => {
  if (typeof window === "undefined") return null;
  return window.SpeechRecognition || window.webkitSpeechRecognition || null;
};

const isMobileDevice = () => {
  if (typeof window === "undefined") return false;
  return "ontouchstart" in window || navigator.maxTouchPoints > 0 || /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) || window.innerWidth < 768;
};

// PDPA: Mask personal data
const maskPII = (text) => {
  if (!text) return text;
  let masked = text.replace(/([a-zA-Z0-9._%+-])([a-zA-Z0-9._%+-]+)@([a-zA-Z0-9])([a-zA-Z0-9.-]+)\.([a-zA-Z]{2,})/g, (m, f, r, df, dr, ext) =>
    f + "*".repeat(Math.min(r.length, 4)) + "@" + df + "*".repeat(Math.min(dr.length, 4)) + "." + ext
  );
  masked = masked.replace(/\b(\d{4})(\d{4})\b/g, "$1****");
  masked = masked.replace(/(\+65\s?)(\d{4})(\d{4})\b/g, "$1$2****");
  return masked;
};

// Strip tool_call blocks and check if message is a tool call
const isToolCallOnly = (text) => /^[\s\S]*```tool_call[\s\S]*```[\s]*$/.test(text);
const containsToolCall = (text) => text && text.includes("```tool_call");
const stripToolCalls = (text) => {
  if (!text) return text;
  return text.replace(/```tool_call[\s\S]*?```/g, "").trim();
};

// Prepare text for TTS — add speech pauses, read phone numbers as digits, clean markdown
const prepareTTSText = (text) => {
  if (!text) return "";
  let t = stripToolCalls(text).replace(/\*\*/g, "").replace(/\*/g, "").trim();
  // Replace newlines with pauses for natural speech breaks
  t = t.replace(/\n\n/g, ". ");
  t = t.replace(/\n/g, ". ");
  t = t.replace(/\.\s*\./g, ".");
  t = t.replace(/\?\s*\./g, "?");
  // Phone with country code: +6596415404 → "plus 6-5. 9-6-4-1. 5-4-0-4"
  t = t.replace(/\+65\s?(\d{4})(\d{4})/g, (_, a, b) =>
    "plus 6-5. " + a.split("").join("-") + ". " + b.split("").join("-")
  );
  // 8-digit SG phone numbers: 96415404 → "9-6-4-1. 5-4-0-4"
  t = t.replace(/\b(\d{4})(\d{4})\b/g, (_, a, b) =>
    a.split("").join("-") + ". " + b.split("").join("-")
  );
  // Karaoke booking codes: BK0015 → "B-K. 0-0-1-5"
  t = t.replace(/\bBK(\d{4})\b/g, (_, num) =>
    "B-K. " + num.split("").join("-")
  );
  // BBQ booking codes: BBQ0009 → "B-B-Q. 0-0-0-9"
  t = t.replace(/\bBBQ(\d{4})\b/g, (_, num) =>
    "B-B-Q. " + num.split("").join("-")
  );
  // Add pause after colons
  t = t.replace(/:\s*/g, ", ");
  // Clean up multiple spaces
  t = t.replace(/\s+/g, " ").trim();
  return t;
};

// Simple markdown: **bold**, *italic*, \n
const renderMarkdown = (text) => {
  if (!text) return null;
  // First mask PII
  let processed = maskPII(text);
  // Strip any remaining tool_call blocks
  processed = stripToolCalls(processed);
  if (!processed) return null;

  // Split by lines, process each
  const parts = [];
  const lines = processed.split("\n");
  lines.forEach((line, li) => {
    // Process bold and italic within line
    const segments = [];
    let remaining = line;
    let key = 0;

    // Process **bold** first, then *italic*
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;
    let match;
    const tempParts = [];

    while ((match = boldRegex.exec(remaining)) !== null) {
      if (match.index > lastIndex) {
        tempParts.push({ type: "text", content: remaining.slice(lastIndex, match.index) });
      }
      tempParts.push({ type: "bold", content: match[1] });
      lastIndex = match.index + match[0].length;
    }
    if (lastIndex < remaining.length) {
      tempParts.push({ type: "text", content: remaining.slice(lastIndex) });
    }

    tempParts.forEach((part) => {
      if (part.type === "bold") {
        segments.push(<strong key={`b${li}-${key++}`}>{part.content}</strong>);
      } else {
        // Process *italic* in text segments
        const italicRegex = /\*(.+?)\*/g;
        let iLast = 0;
        let iMatch;
        while ((iMatch = italicRegex.exec(part.content)) !== null) {
          if (iMatch.index > iLast) segments.push(part.content.slice(iLast, iMatch.index));
          segments.push(<em key={`i${li}-${key++}`}>{iMatch[1]}</em>);
          iLast = iMatch.index + iMatch[0].length;
        }
        if (iLast < part.content.length) segments.push(part.content.slice(iLast));
      }
    });

    parts.push(...segments);
    if (li < lines.length - 1) parts.push(<br key={`br${li}`} />);
  });

  return parts;
};

const LANGUAGES = [
  { code: "en", label: "English", flag: "EN" },
  { code: "zh", label: "中文", flag: "中" },
  { code: "ms", label: "Malay", flag: "MY" },
  { code: "ta", label: "தமிழ்", flag: "த" },
];

const ChatBubble = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(isMobileDevice);
  const [lang, setLang] = useState(null); // null = not chosen yet, then "en"/"zh"/"ms"/"ta"
  const [pdpaAgreed, setPdpaAgreed] = useState(false);
  const langRef = useRef(null);
  const [messages, setMessages] = useState([
    { role: "assistant", content: "Hi! Welcome to YCK Court RN.\n\nPlease select your language:" }
  ]);
  const [input, setInput] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => `chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const [isExpired, setIsExpired] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [voiceMode, setVoiceMode] = useState(null);
  const [speakingIdx, setSpeakingIdx] = useState(null);
  const [isMuted, setIsMuted] = useState(false);
  const speakIdRef = useRef(0); // Incrementing ID to prevent stale callback race conditions
  const lastActivityRef = useRef(Date.now());
  const timerRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const recognitionRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null); // For TTS playback
  const pendingVoiceSendRef = useRef(null); // For auto-send after voice
  const audioUnlockedRef = useRef(false); // Track if audio has been unlocked by user gesture

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (isOpen && inputRef.current && !isExpired) inputRef.current.focus();
  }, [isOpen, isExpired]);

  // Session timeout
  useEffect(() => {
    if (!isOpen || isExpired) return;
    const checkExpiry = () => {
      const elapsed = Date.now() - lastActivityRef.current;
      if (elapsed >= SESSION_TIMEOUT_MS && messages.length > 1) {
        setIsExpired(true);
        setMessages(prev => [...prev, { role: "system", content: "This chat session has expired due to inactivity.\n\nThank you for using YCK Court RN Assistant! If you need further help, please start a new chat.\n\nHave a great day!" }]);
      }
    };
    checkExpiry();
    timerRef.current = setInterval(checkExpiry, 30000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [isOpen, isExpired, messages.length]);

  const resetActivity = useCallback(() => { lastActivityRef.current = Date.now(); }, []);

  // Auto-send after voice input completes
  useEffect(() => {
    if (!pendingVoiceSendRef.current) return;
    if (isLoading || isRecording || isTranscribing) return;
    
    const text = pendingVoiceSendRef.current;
    pendingVoiceSendRef.current = null;
    if (text.trim()) {
      // Brief delay so user sees the transcribed text, then auto-send
      const timer = setTimeout(() => {
        sendMessage(text);
      }, 300);
      return () => clearTimeout(timer);
    }
  }); // No deps — run every render to catch the ref change immediately

  // Auto-speak tracking
  const lastSpokenIdxRef = useRef(-1);
  const pendingSpeakRef = useRef(null); // {text, idx} — set when AI finishes, spoken on next tick

  // Process pending speak — this runs frequently and catches completed messages
  useEffect(() => {
    if (isMuted || !isOpen || !pendingSpeakRef.current) return;
    const { text, idx } = pendingSpeakRef.current;
    pendingSpeakRef.current = null;
    if (idx > lastSpokenIdxRef.current) {
      lastSpokenIdxRef.current = idx;
      speakMessage(text, idx);
    }
  });

  // Detect when an AI message finishes streaming and queue it for speaking
  useEffect(() => {
    if (isMuted || !isOpen) return;
    const lastIdx = messages.length - 1;
    const lastMsg = messages[lastIdx];
    if (lastMsg && lastMsg.role === "assistant" && !lastMsg.streaming && lastMsg.content && lastIdx > lastSpokenIdxRef.current) {
      const cleaned = stripToolCalls(lastMsg.content);
      if (cleaned && cleaned.length > 5) {
        pendingSpeakRef.current = { text: lastMsg.content, idx: lastIdx };
      }
    }
  }, [messages, isMuted, isOpen]);

  const startNewChat = useCallback(() => {
    setSessionId(`chat-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
    setMessages([{ role: "assistant", content: "Hi! Welcome to YCK Court RN.\n\nPlease select your language:" }]);
    setIsExpired(false);
    setInput("");
    setLang(null);
    langRef.current = null;
    setPdpaAgreed(false);
    lastActivityRef.current = Date.now();
  }, []);

  // Unlock audio on first user interaction (required by mobile browsers)
  const unlockAudio = useCallback(() => {
    if (audioUnlockedRef.current) return;
    audioUnlockedRef.current = true;
    // Warm up speechSynthesis and pre-load voices
    if (window.speechSynthesis) {
      window.speechSynthesis.getVoices(); // Trigger voice loading
      const u = new SpeechSynthesisUtterance("");
      u.volume = 0;
      window.speechSynthesis.speak(u);
    }
    // Warm up AudioContext
    try { const ctx = new (window.AudioContext || window.webkitAudioContext)(); ctx.resume(); } catch {}
  }, []);

  // ── TTS: Speak a message ──
  const speakMessage = useCallback(async (text, msgIdx) => {
    // Stop any current playback — clear callbacks FIRST to prevent race condition
    if (audioRef.current) {
      audioRef.current.onended = null;
      audioRef.current.onerror = null;
      audioRef.current.pause();
      audioRef.current = null;
    }
    if (window.speechSynthesis) window.speechSynthesis.cancel();

    // Toggle off if tapping the same message
    if (speakingIdx === msgIdx) { setSpeakingIdx(null); return; }

    const cleanText = prepareTTSText(text);
    if (!cleanText) return;

    // Unique ID for this speak — stale callbacks check against this
    const thisSpeak = ++speakIdRef.current;
    setSpeakingIdx(msgIdx);

    const onDone = () => {
      if (speakIdRef.current === thisSpeak) setSpeakingIdx(null);
      if (audioRef.current) audioRef.current = null;
    };

    const currentLang = langRef.current || "en";

    // Malay: Use browser TTS with ms-MY locale (free + correct pronunciation)
    // OpenAI TTS cannot handle Malay Latin script — always reads it as English
    if (currentLang === "ms" && window.speechSynthesis) {
      try {
        const utterance = new SpeechSynthesisUtterance(cleanText.slice(0, 1500));
        utterance.lang = "ms-MY";
        utterance.rate = 0.95;
        // Try to find a Malay voice
        const voices = window.speechSynthesis.getVoices();
        const malayVoice = voices.find(v => v.lang === "ms-MY" || v.lang === "ms" || v.lang.startsWith("ms"));
        if (malayVoice) utterance.voice = malayVoice;
        utterance.onend = onDone;
        utterance.onerror = onDone;
        window.speechSynthesis.speak(utterance);
        return;
      } catch (e) {
        console.log("Browser Malay TTS failed:", e);
      }
    }

    // English / Tamil / Chinese: OpenAI TTS (natural voice)
    const preAudio = new Audio();
    preAudio.onended = onDone;
    preAudio.onerror = onDone;
    audioRef.current = preAudio;

    try {
      const res = await fetch(`${API}/chat/tts`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: cleanText.slice(0, 2000), language: currentLang })
      });
      if (speakIdRef.current !== thisSpeak) return;
      if (res.ok) {
        const data = await res.json();
        if (data.audio) {
          preAudio.src = `data:audio/mp3;base64,${data.audio}`;
          try { await preAudio.play(); return; } catch {}
        }
      }
    } catch (e) {
      console.log("TTS failed:", e);
    }

    onDone();
  }, [speakingIdx]);

  // ── Voice Input: Browser Speech API ──
  const startBrowserSpeech = useCallback(() => {
    const SpeechRecognition = getSpeechRecognition();
    if (!SpeechRecognition) return false;
    try {
      const recognition = new SpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = true;
      recognition.maxAlternatives = 1;
      recognition.lang = langRef.current === "ms" ? "ms-MY" : langRef.current === "zh" ? "zh-CN" : langRef.current === "ta" ? "ta-IN" : "en-SG";
      let finalTranscript = "";
      recognition.onresult = (event) => {
        let interim = "";
        for (let i = event.resultIndex; i < event.results.length; i++) {
          const t = event.results[i][0].transcript;
          if (event.results[i].isFinal) finalTranscript += t + " ";
          else interim = t;
        }
        setInput(finalTranscript + interim);
      };
      recognition.onend = () => {
        setIsRecording(false);
        setVoiceMode(null);
        if (finalTranscript.trim()) {
          setInput(finalTranscript.trim());
          pendingVoiceSendRef.current = finalTranscript.trim();
        }
      };
      recognition.onerror = (event) => { setIsRecording(false); setVoiceMode(null); if (event.error !== "aborted") startWhisperRecording(); };
      recognition.start();
      recognitionRef.current = recognition;
      setIsRecording(true);
      setVoiceMode("browser");
      return true;
    } catch (e) { return false; }
  }, []);

  // ── Voice Input: Whisper fallback ──
  const startWhisperRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : "audio/mp4";
      const mediaRecorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      mediaRecorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      mediaRecorder.onstop = async () => {
        stream.getTracks().forEach(t => t.stop());
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        if (blob.size < 100) { setIsRecording(false); setVoiceMode(null); return; }
        setIsTranscribing(true);
        try {
          const formData = new FormData();
          formData.append("file", blob, mimeType === "audio/mp4" ? "voice.mp4" : "voice.webm");
          const res = await fetch(`${API}/chat/transcribe`, { method: "POST", body: formData });
          if (res.ok) {
            const data = await res.json();
            if (data.text) {
              const finalText = data.text;
              setInput(finalText);
              pendingVoiceSendRef.current = finalText;
            }
          }
        } catch (err) { console.error("Whisper error:", err); }
        setIsTranscribing(false);
        setIsRecording(false);
        setVoiceMode(null);
      };
      mediaRecorder.start();
      mediaRecorderRef.current = mediaRecorder;
      setIsRecording(true);
      setVoiceMode("whisper");
    } catch (err) { setIsRecording(false); setVoiceMode(null); }
  }, []);

  const toggleVoice = useCallback(() => {
    if (isRecording) {
      if (voiceMode === "browser" && recognitionRef.current) recognitionRef.current.stop();
      else if (voiceMode === "whisper" && mediaRecorderRef.current) mediaRecorderRef.current.stop();
      return;
    }
    if (!startBrowserSpeech()) startWhisperRecording();
  }, [isRecording, voiceMode, startBrowserSpeech, startWhisperRecording]);

  // ── Send message ──
  const sendMessage = async (directText) => {
    const text = (typeof directText === "string" ? directText : input).trim();
    if (!text || isLoading || isExpired) return;
    unlockAudio(); // Unlock audio on user gesture
    resetActivity();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: text }]);
    setIsLoading(true);
    setMessages(prev => [...prev, { role: "assistant", content: "", streaming: true }]);

    try {
      const response = await fetch(`${API}/chat/message`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ message: text, session_id: sessionId, language: lang || "en", pdpa_agreed: pdpaAgreed }),
      });

      if (response.status === 429) {
        setMessages(prev => { const u = [...prev]; const l = u[u.length - 1]; if (l && l.streaming) { l.content = "You're sending messages too quickly. Please wait a moment."; l.streaming = false; } return [...u]; });
        setIsLoading(false);
        return;
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let fullText = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split("\n");
        buffer = lines.pop() || "";

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === "text") {
                fullText += data.content;
                setMessages(prev => { const u = [...prev]; const l = u[u.length - 1]; if (l && l.streaming) l.content = fullText; return [...u]; });
              } else if (data.type === "clear") {
                fullText = "";
                setMessages(prev => { const u = [...prev]; const l = u[u.length - 1]; if (l && l.streaming) l.content = ""; return [...u]; });
              } else if (data.type === "done") {
                resetActivity();
                setMessages(prev => { const u = [...prev]; const l = u[u.length - 1]; if (l && l.streaming) l.streaming = false; return [...u]; });
                // Auto-speak: trigger directly in the send gesture chain
                if (!isMuted && fullText) {
                  const cleaned = stripToolCalls(fullText);
                  if (cleaned && cleaned.length > 5) {
                    const idx = messages.length + 1; // +1 for user msg, +1 for this AI msg (0-based)
                    lastSpokenIdxRef.current = idx;
                    speakMessage(fullText, idx);
                  }
                }
              }
            } catch {}
          }
        }
      }
      setMessages(prev => { const u = [...prev]; const l = u[u.length - 1]; if (l && l.streaming) l.streaming = false; return [...u]; });
    } catch {
      setMessages(prev => { const u = [...prev]; const l = u[u.length - 1]; if (l && l.streaming) { l.content = "Sorry, I'm having trouble connecting. Please try again."; l.streaming = false; } return [...u]; });
    }
    setIsLoading(false);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") {
      if (isMobile) return;
      if (!e.shiftKey) { e.preventDefault(); sendMessage(); }
    }
  };

  const suggestionsMap = {
    en: ["Book Karaoke", "Book BBQ Pit", "Check prices", "What dates available?"],
    zh: ["预订卡拉OK", "预订烧烤", "查看价格", "哪些日期有空？"],
    ms: ["Tempah Karaoke", "Tempah BBQ", "Semak harga", "Tarikh yang tersedia?"],
    ta: ["கரோக்கி முன்பதிவு", "BBQ முன்பதிவு", "விலை சரிபார்", "எந்த தேதிகள் உள்ளன?"],
  };
  const suggestions = suggestionsMap[lang] || suggestionsMap.en;

  // Determine what to show for a message
  const getDisplayContent = (msg) => {
    if (!msg.content) return null;
    // If it's a streaming message that contains only tool_call, show thinking indicator
    if (msg.streaming && containsToolCall(msg.content)) {
      return <span className="flex items-center gap-2 text-purple-600"><Loader2 size={14} className="animate-spin" /> Checking...</span>;
    }
    // For completed messages, strip any lingering tool_call blocks
    const cleaned = stripToolCalls(msg.content);
    if (!cleaned && !msg.streaming) return null;
    return renderMarkdown(cleaned || "");
  };

  return (
    <>
      {!isOpen && (
        <button onClick={() => { unlockAudio(); setIsOpen(true); if (!isMuted) { lastSpokenIdxRef.current = 0; speakMessage(messages[0]?.content || "", 0); } }} className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-purple-600 text-white rounded-full shadow-lg hover:bg-purple-700 transition-all hover:scale-110 flex items-center justify-center border-2 border-black" data-testid="chat-bubble-btn">
          <MessageCircle size={26} />
        </button>
      )}

      {isOpen && (
        <>
          {/* Backdrop overlay on mobile — keeps focus on chat */}
          {isMobile && (
            <div className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm" data-testid="chat-backdrop" />
          )}
          <div className={`fixed z-50 flex flex-col bg-white shadow-2xl border-2 border-black ${isMobile ? "bottom-0 left-0 right-0 max-h-[85vh] rounded-t-2xl" : "bottom-4 right-4 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] border-4"}`} data-testid="chat-window">
          {/* Header */}
          <div className="bg-purple-600 text-white px-4 py-3 flex items-center justify-between border-b-4 border-black flex-shrink-0">
            <div className="flex items-center gap-2">
              <Bot size={20} />
              <div>
                <h3 className="font-bold text-sm">YCK Court RN Assistant</h3>
                <p className="text-[10px] text-purple-200">Book Karaoke & BBQ | Ask anything</p>
              </div>
            </div>
            <div className="flex items-center gap-1">
              {/* New Chat button — always visible after conversation starts */}
              {lang && messages.length > 2 && (
                <button onClick={startNewChat} className="p-1 hover:bg-white/20 rounded" data-testid="chat-new-btn" title="New chat">
                  <RotateCcw size={15} />
                </button>
              )}
              {/* Mute/unmute toggle */}
              <button
                onClick={() => {
                  setIsMuted(m => {
                    if (!m) {
                      // Muting — stop everything, increment speakId to invalidate pending speaks
                      speakIdRef.current++;
                      if (audioRef.current) {
                        audioRef.current.onended = null;
                        audioRef.current.onerror = null;
                        audioRef.current.pause();
                        audioRef.current = null;
                      }
                      if (window.speechSynthesis) window.speechSynthesis.cancel();
                      setSpeakingIdx(null);
                    }
                    return !m;
                  });
                }}
                className="p-1 hover:bg-white/20 rounded"
                data-testid="chat-mute-btn"
                title={isMuted ? "Unmute AI voice" : "Mute AI voice"}
              >
                {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
              </button>
              <button onClick={() => setIsOpen(false)} className="p-1 hover:bg-white/20 rounded" data-testid="chat-close-btn" title="Minimize chat">
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-3 space-y-3 bg-gray-50" data-testid="chat-messages">
            {messages.map((msg, i) => (
              <div key={i} className={`flex gap-2 ${msg.role === "user" ? "flex-row-reverse" : "flex-row"}`}>
                {msg.role === "system" ? (
                  <div className="w-full px-4 py-3 bg-amber-50 border-2 border-amber-300 rounded-xl text-sm text-amber-800 text-center leading-relaxed" data-testid="chat-session-expired">
                    {msg.content}
                  </div>
                ) : (
                  <>
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 ${msg.role === "user" ? "bg-blue-500" : "bg-purple-500"}`}>
                      {msg.role === "user" ? <User size={14} className="text-white" /> : <Bot size={14} className="text-white" />}
                    </div>
                    <div className="max-w-[75%]">
                      <div className={`px-3 py-2 text-sm leading-relaxed ${msg.role === "user" ? "bg-blue-500 text-white rounded-2xl rounded-tr-sm" : "bg-white border border-gray-200 text-gray-800 rounded-2xl rounded-tl-sm shadow-sm"}`}>
                        {getDisplayContent(msg)}
                        {msg.streaming && !containsToolCall(msg.content || "") && (
                          <span className="inline-block ml-1 animate-pulse">|</span>
                        )}
                      </div>
                    </div>
                  </>
                )}
              </div>
            ))}

            {/* Language selector — shown when no language chosen yet */}
            {!lang && !isLoading && !isExpired && (
              <div className="flex flex-col items-center gap-2 mt-2">
                <div className="flex flex-wrap justify-center gap-2">
                  {LANGUAGES.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        unlockAudio();
                        setLang(l.code);
                        langRef.current = l.code;
                        // Show PDPA notice in chosen language
                        const pdpaMap = {
                          en: "Before we proceed, please note:\n\nYour personal data (name, email, phone, address) will be collected solely for the purpose of processing your booking. Data will be retained for up to 1 year after your last booking and handled in accordance with PDPA.\n\nDo you agree to proceed?",
                          zh: "在我们继续之前，请注意：\n\n您的个人数据（姓名、电子邮件、电话、地址）将仅用于处理您的预订。数据将在您最后一次预订后保留最多1年，并按照PDPA处理。\n\n您同意继续吗？",
                          ms: "Sebelum kita teruskan, sila ambil perhatian:\n\nData peribadi anda (nama, emel, telefon, alamat) akan dikumpul semata-mata untuk memproses tempahan anda. Data akan disimpan sehingga 1 tahun selepas tempahan terakhir anda dan dikendalikan mengikut PDPA.\n\nAdakah anda bersetuju untuk teruskan?",
                          ta: "தொடர்வதற்கு முன், தயவுசெய்து கவனிக்கவும்:\n\nஉங்கள் தனிப்பட்ட தரவு (பெயர், மின்னஞ்சல், தொலைபேசி, முகவரி) உங்கள் முன்பதிவை செயலாக்குவதற்காக மட்டுமே சேகரிக்கப்படும். தரவு உங்கள் கடைசி முன்பதிவுக்குப் பிறகு 1 ஆண்டு வரை PDPA-ன் படி வைக்கப்படும்.\n\nதொடர ஒப்புக்கொள்கிறீர்களா?"
                        };
                        setMessages(prev => [...prev, { role: "assistant", content: pdpaMap[l.code] }]);
                        // Speak the PDPA notice
                        setTimeout(() => {
                          const idx = 1;
                          lastSpokenIdxRef.current = idx;
                          speakMessage(pdpaMap[l.code], idx);
                        }, 200);
                      }}
                      className="px-4 py-2 bg-purple-100 text-purple-700 text-sm font-bold rounded-full hover:bg-purple-200 transition-colors border-2 border-purple-300"
                      data-testid={`chat-lang-${l.code}`}
                    >
                      {l.flag} {l.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* PDPA consent buttons — shown after language selected but before agreeing */}
            {lang && !pdpaAgreed && messages.length === 2 && !isLoading && !isExpired && (
              <div className="flex gap-2 mt-2 justify-center">
                <button
                  onClick={() => {
                    setPdpaAgreed(true);
                    const welcomeMap = {
                      en: "Thank you! I can help you book a Karaoke room or BBQ pit, check availability, or answer questions.\n\nWhat would you like to do today?",
                      zh: "谢谢！我可以帮您预订卡拉OK房间或烧烤设施。\n\n您今天想做什么？",
                      ms: "Terima kasih! Saya boleh membantu anda menempah bilik Karaoke atau tempat BBQ.\n\nApa yang anda ingin lakukan hari ini?",
                      ta: "நன்றி! கரோக்கி அறை அல்லது BBQ இடம் முன்பதிவு செய்ய நான் உதவ முடியும்.\n\nஇன்று நீங்கள் என்ன செய்ய விரும்புகிறீர்கள்?"
                    };
                    setMessages(prev => [
                      ...prev,
                      { role: "user", content: lang === "en" ? "Yes, I agree" : lang === "zh" ? "同意" : lang === "ms" ? "Ya, saya setuju" : "ஆம், ஒப்புக்கொள்கிறேன்" },
                      { role: "assistant", content: welcomeMap[lang] }
                    ]);
                    setTimeout(() => {
                      const idx = 3;
                      lastSpokenIdxRef.current = idx;
                      speakMessage(welcomeMap[lang], idx);
                    }, 200);
                  }}
                  className="px-4 py-2 bg-green-500 text-white text-sm font-bold rounded-full hover:bg-green-600 transition-colors"
                  data-testid="chat-pdpa-agree"
                >
                  {lang === "en" ? "Yes, I agree" : lang === "zh" ? "同意" : lang === "ms" ? "Ya, setuju" : "ஆம், ஒப்புக்கொள்கிறேன்"}
                </button>
                <button
                  onClick={() => {
                    const declineMap = {
                      en: "No problem. You can ask general questions about our facilities, pricing, and availability. However, to make a booking, PDPA consent is required.\n\nFeel free to ask anything!",
                      zh: "没问题。您可以询问有关设施、价格和可用时间的一般问题。但是，要进行预订，需要PDPA同意。\n\n随时提问！",
                      ms: "Tiada masalah. Anda boleh bertanya soalan umum tentang kemudahan, harga dan ketersediaan. Walau bagaimanapun, untuk membuat tempahan, persetujuan PDPA diperlukan.\n\nSila tanya apa sahaja!",
                      ta: "பரவாயில்லை. வசதிகள், விலை மற்றும் கிடைக்கும் நேரம் பற்றி பொதுவான கேள்விகளைக் கேட்கலாம். ஆனால், முன்பதிவு செய்ய PDPA ஒப்புதல் தேவை.\n\nஎதையும் கேளுங்கள்!"
                    };
                    setMessages(prev => [
                      ...prev,
                      { role: "user", content: lang === "en" ? "No, thanks" : lang === "zh" ? "不同意" : lang === "ms" ? "Tidak" : "இல்லை" },
                      { role: "assistant", content: declineMap[lang] }
                    ]);
                    // Do NOT set pdpaAgreed — booking buttons won't show
                  }}
                  className="px-4 py-2 bg-gray-200 text-gray-700 text-sm font-bold rounded-full hover:bg-gray-300 transition-colors"
                  data-testid="chat-pdpa-decline"
                >
                  {lang === "en" ? "No, thanks" : lang === "zh" ? "不同意" : lang === "ms" ? "Tidak" : "இல்லை"}
                </button>
              </div>
            )}

            {/* Quick suggestions — ONLY shown after PDPA consent agreed */}
            {lang && pdpaAgreed && messages.length >= 3 && messages.length <= 4 && !isLoading && !isExpired && (
              <div className="flex flex-wrap gap-2 mt-2">
                {suggestions.map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)} className="px-3 py-1.5 bg-purple-100 text-purple-700 text-xs font-bold rounded-full hover:bg-purple-200 transition-colors border border-purple-300" data-testid={`chat-suggestion-${i}`}>{s}</button>
                ))}
              </div>
            )}
            
            {/* General question suggestions — shown after PDPA declined */}
            {lang && !pdpaAgreed && messages.length >= 3 && messages.length <= 4 && !isLoading && !isExpired && (
              <div className="flex flex-wrap gap-2 mt-2">
                {(lang === "zh" ? ["查看价格", "哪些日期有空？"] : lang === "ms" ? ["Semak harga", "Tarikh yang tersedia?"] : lang === "ta" ? ["விலை சரிபார்", "எந்த தேதிகள் உள்ளன?"] : ["Check prices", "What dates available?"]).map((s, i) => (
                  <button key={i} onClick={() => sendMessage(s)} className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-bold rounded-full hover:bg-gray-200 transition-colors border border-gray-300" data-testid={`chat-general-${i}`}>{s}</button>
                ))}
              </div>
            )}
            {/* New Booking button — appears after booking is completed */}
            {(() => {
              const lastAssistant = [...messages].reverse().find(m => m.role === "assistant" && !m.streaming);
              const hasBookingCode = lastAssistant && /\b(BK|BBQ)\d{4}\b/.test(lastAssistant.content || "");
              if (hasBookingCode && !isLoading) {
                const newBookingLabel = {
                  en: "Make Another Booking",
                  zh: "再次预订",
                  ms: "Buat Tempahan Baru",
                  ta: "புதிய முன்பதிவு"
                };
                return (
                  <div className="flex justify-center mt-3">
                    <button
                      onClick={startNewChat}
                      className="flex items-center gap-2 px-4 py-2 bg-green-500 text-white text-sm font-bold rounded-full hover:bg-green-600 transition-colors"
                      data-testid="chat-new-booking-btn"
                    >
                      <RotateCcw size={14} />
                      {newBookingLabel[lang] || newBookingLabel.en}
                    </button>
                  </div>
                );
              }
              return null;
            })()}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="border-t-2 border-gray-200 p-3 bg-white flex-shrink-0">
            {isExpired ? (
              <button onClick={startNewChat} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm" data-testid="chat-restart-btn">
                <RotateCcw size={16} /> Start New Chat
              </button>
            ) : (
              <>
                {(isRecording || isTranscribing) && (
                  <div className="flex items-center gap-2 mb-2 px-2 py-1.5 bg-red-50 border border-red-200 rounded-lg" data-testid="chat-recording-indicator">
                    <span className="relative flex h-3 w-3">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500"></span>
                    </span>
                    <span className="text-xs text-red-700 font-medium">
                      {isTranscribing ? "Transcribing..." : voiceMode === "whisper" ? "Recording... Tap mic to stop" : "Listening... Speak now"}
                    </span>
                  </div>
                )}
                <div className="flex gap-1.5 items-end">
                  <textarea
                    ref={inputRef}
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder={isRecording ? "Listening..." : isMobile ? "Type your message..." : "Shift+Enter for new line"}
                    className="flex-1 min-w-0 px-3 py-2 border-2 border-gray-300 rounded-lg text-sm focus:border-purple-500 focus:outline-none resize-none min-h-[38px] max-h-[100px]"
                    disabled={isLoading || isTranscribing}
                    data-testid="chat-input"
                    rows={1}
                    style={{ height: "auto", overflow: "hidden" }}
                    onInput={(e) => { e.target.style.height = "auto"; e.target.style.height = Math.min(e.target.scrollHeight, 100) + "px"; }}
                  />
                  <button onClick={toggleVoice} disabled={isLoading || isTranscribing} className={`flex-shrink-0 w-10 h-10 flex items-center justify-center rounded-lg transition-colors ${isRecording ? "bg-red-500 text-white hover:bg-red-600 animate-pulse" : "bg-gray-100 text-gray-600 hover:bg-gray-200 border-2 border-gray-300"} disabled:opacity-50 disabled:cursor-not-allowed`} data-testid="chat-mic-btn" title={isRecording ? "Stop recording" : "Voice input"}>
                    {isTranscribing ? <Loader2 size={18} className="animate-spin" /> : isRecording ? <MicOff size={18} /> : <Mic size={18} />}
                  </button>
                  <button onClick={sendMessage} disabled={!input.trim() || isLoading || isRecording} className="flex-shrink-0 w-10 h-10 flex items-center justify-center bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors" data-testid="chat-send-btn">
                    {isLoading ? <Loader2 size={18} className="animate-spin" /> : <Send size={18} />}
                  </button>
                </div>
                <p className="text-[10px] text-gray-400 mt-1 text-center">Powered by AI | EN, Malay, Tamil, 中文, Singlish</p>
              </>
            )}
          </div>
        </div>
        </>
      )}
    </>
  );
};

export default ChatBubble;
